import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { resolveMediaUrl } from "@/components/FileUpload";
import { ArrowRight, CheckCircle2, Copy, ExternalLink, Loader2, Link2, Palette, Plus, Send, Trash2, X, Upload, Mail, Clock } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { useAccount } from "@/context/AccountContext";
import { useNavigate } from "react-router-dom";

const STATUS = [
  { key: "open", label: "Offen", cls: "bg-blue-100 text-blue-800 border-blue-200" },
  { key: "in_progress", label: "In Arbeit", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  { key: "question", label: "Rückfrage", cls: "bg-violet-100 text-violet-800 border-violet-200" },
  { key: "done", label: "Fertig", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
];
const FORMAT_LABEL = { flyer: "Flyer", plakat: "Plakat", social: "Social-Media" };
const EMPTY_INTERNAL_DESIGN = { event_title: "", description: "", deadline: "", formats: ["social"], assigned_to: "", priority: "medium", images: [] };
const statusOf = (k) => STATUS.find((s) => s.key === k) || STATUS[0];
const fmtDeadline = (d) => { try { const [y, m, day] = d.split("-"); return `${day}.${m}.${y}`; } catch { return d; } };

export default function DesignRequests() {
  const { user } = useAuth();
  const { accounts, currentKey } = useAccount();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);
  const [internalOpen, setInternalOpen] = useState(false);

  const load = () => api.get("/design-requests").then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const copyLink = async () => {
    const url = `${window.location.origin}/design-anfrage`;
    try { await navigator.clipboard.writeText(url); toast.success("Externer Anfrage-Link kopiert — bereit für Kunden und Partner"); }
    catch { toast.error(url); }
  };

  const visible = items.filter((i) => filter === "all" || i.status === filter);

  return (
    <div className="space-y-5" data-testid="design-requests-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 flex items-center gap-2"><Palette className="h-6 w-6 text-violet-600" /> Design-Aufträge</h1>
          <p className="text-sm text-neutral-500 mt-0.5">Externe Entwürfe prüfen und anschließend kontrolliert in eure Content-Pipeline übernehmen</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="design-create-internal" onClick={() => setInternalOpen(true)} className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-xl bg-neutral-900 text-white hover:bg-neutral-800 transition">
            <Plus className="h-4 w-4" /> Interner Designauftrag
          </button>
          <button data-testid="design-copy-link" onClick={copyLink} className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-xl bg-violet-600 text-white hover:bg-violet-700 transition">
            <Link2 className="h-4 w-4" /> Externen Anfrage-Link kopieren
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-cyan-200 bg-cyan-50/80 px-4 py-3 text-sm text-cyan-950" data-testid="design-workflow-hint">
        <strong>Ein Link, ein klarer Ablauf:</strong> Kunde oder Partner erstellt den Entwurf ohne Board-Login. Die Anfrage landet zuerst hier zur internen Prüfung. Erst mit „In Pipeline übernehmen“ entsteht genau eine Karte im ausgewählten Kanal.
      </div>

      <div className="flex gap-2 flex-wrap">
        {[{ key: "all", label: `Alle (${items.length})` }, ...STATUS.map((s) => ({ key: s.key, label: `${s.label} (${items.filter((i) => i.status === s.key).length})` }))].map((c) => (
          <button key={c.key} data-testid={`design-filter-${c.key}`} onClick={() => setFilter(c.key)}
            className={`px-3.5 py-2 rounded-full text-xs font-bold transition ${filter === c.key ? "bg-neutral-900 text-white" : "bg-white border border-neutral-200 text-neutral-500 hover:border-neutral-300"}`}>
            {c.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-16 flex justify-center text-neutral-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : visible.length === 0 ? (
        <div className="py-16 text-center text-sm text-neutral-400" data-testid="design-empty">Noch keine Anfragen — teile den Formular-Link mit dem Team.</div>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {visible.map((r) => {
            const st = statusOf(r.status);
            return (
              <button key={r.id} data-testid={`design-card-${r.id}`} onClick={() => setActive(r)}
                className="text-left bg-white rounded-2xl border border-neutral-100 shadow-sm p-4 hover:shadow-md hover:border-violet-200 transition">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold text-neutral-900 text-sm leading-snug">{r.event_title}</h3>
                  <span className={`shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full border ${st.cls}`}>{st.label}</span>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {(r.formats || []).map((fm) => (
                    <span key={fm} className="text-[10px] font-semibold bg-violet-50 text-violet-700 px-2 py-0.5 rounded-full">{FORMAT_LABEL[fm] || fm}</span>
                  ))}
                  {r.deadline && <span className="inline-flex items-center gap-0.5 text-[10px] font-bold bg-rose-50 text-rose-600 px-2 py-0.5 rounded-full"><Clock className="h-3 w-3" /> {fmtDeadline(r.deadline)}</span>}
                  {(r.images || []).length > 0 && <span className="text-[10px] text-neutral-400">📎 {(r.images || []).length} Datei(en)</span>}
                  {(r.results || []).length > 0 && <span className="text-[10px] font-semibold text-emerald-600">✅ {(r.results || []).length} Design(s)</span>}
                  {(r.comments || []).length > 0 && <span className="text-[10px] font-semibold text-amber-600" data-testid={`design-card-comments-${r.id}`}>📝 {(r.comments || []).length} Feedback</span>}
                </div>
                <p className="text-xs text-neutral-500 mt-2 line-clamp-2">{r.description || "—"}</p>
                <div className="text-[11px] text-neutral-400 mt-2 flex items-center justify-between">
                  <span>{r.contact_name || "Ohne Name"}</span>
                  {r.assigned_to && <span className="font-semibold text-neutral-500">→ {r.assigned_to}</span>}
                </div>
              </button>
            );
          })}
        </div>
      )}

      <DesignDetail item={active} accounts={accounts} currentKey={currentKey} onClose={() => setActive(null)} onChanged={(u) => { setItems((p) => p.map((x) => (x.id === u.id ? u : x))); setActive(u); }} onDeleted={(id) => { setItems((p) => p.filter((x) => x.id !== id)); setActive(null); }} isAdmin={user?.role === "admin"} />
      <InternalDesignDialog open={internalOpen} currentKey={currentKey} onClose={() => setInternalOpen(false)} onCreated={(item) => { setItems((previous) => [item, ...previous]); setFilter("open"); setInternalOpen(false); setActive(item); }} />
    </div>
  );
}

function InternalDesignDialog({ open, currentKey, onClose, onCreated }) {
  const [form, setForm] = useState(EMPTY_INTERNAL_DESIGN);
  const [members, setMembers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ ...EMPTY_INTERNAL_DESIGN, formats: ["social"], images: [] });
    api.get("/chat/members").then(({ data }) => setMembers(data)).catch(() => setMembers([]));
  }, [open]);

  if (!open) return null;
  const toggleFormat = (value) => setForm((current) => ({ ...current, formats: current.formats.includes(value) ? current.formats.filter((item) => item !== value) : [...current.formats, value] }));
  const uploadFiles = async (event) => {
    const files = Array.from(event.target.files || []); event.target.value = "";
    if (!files.length) return;
    setUploading(true);
    let images = [...form.images];
    for (const file of files) {
      if (file.size > 15 * 1024 * 1024) { toast.error(`„${file.name}“ ist größer als 15 MB`); continue; }
      const data = new FormData(); data.append("file", file);
      try {
        const response = await api.post("/public/design-uploads", data, { timeout: 120000 });
        images.push(response.data);
      } catch (error) { toast.error(formatApiErrorDetail(error.response?.data?.detail) || `Upload von „${file.name}“ fehlgeschlagen`); }
    }
    setForm((current) => ({ ...current, images })); setUploading(false);
  };
  const submit = async () => {
    if (!form.event_title.trim()) return toast.error("Bitte einen Titel angeben");
    if (!form.formats.length) return toast.error("Bitte mindestens ein Format auswählen");
    setSaving(true);
    try {
      const { data } = await api.post("/design-requests", { ...form, account_key: currentKey || "" });
      toast.success("Interner Designauftrag angelegt"); onCreated(data);
    } catch (error) { toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Designauftrag konnte nicht angelegt werden"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[80] bg-black/35 backdrop-blur-sm p-3 sm:p-6 flex items-center justify-center" data-testid="internal-design-dialog" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-3xl border border-white/70 bg-white/95 shadow-2xl p-5 sm:p-7">
        <div className="flex items-start justify-between gap-4">
          <div><h2 className="text-xl font-bold text-neutral-900">Interner Designauftrag</h2><p className="text-sm text-neutral-500 mt-1">Der Auftrag landet direkt unter „Offen“ und wird noch nicht in die Pipeline übernommen.</p></div>
          <button onClick={onClose} className="rounded-full p-2 hover:bg-neutral-100" aria-label="Schließen"><X className="h-5 w-5" /></button>
        </div>
        <div className="mt-5 space-y-4">
          <label className="block text-xs font-bold text-neutral-600">Titel *<input data-testid="internal-design-title" value={form.event_title} onChange={(event) => setForm({ ...form, event_title: event.target.value })} className="mt-1.5 w-full h-11 rounded-xl border border-neutral-200 px-3 text-sm" placeholder="Was soll gestaltet werden?" /></label>
          <label className="block text-xs font-bold text-neutral-600">Beschreibung<textarea data-testid="internal-design-description" value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} className="mt-1.5 w-full min-h-28 rounded-xl border border-neutral-200 p-3 text-sm resize-y" placeholder="Inhalte, Ziel, gewünschte Wirkung und wichtige Hinweise" /></label>
          <div><div className="text-xs font-bold text-neutral-600 mb-2">Formate *</div><div className="flex flex-wrap gap-2">{Object.entries(FORMAT_LABEL).map(([key, label]) => <button type="button" key={key} onClick={() => toggleFormat(key)} className={`rounded-full border px-3 py-2 text-xs font-bold ${form.formats.includes(key) ? "border-violet-600 bg-violet-600 text-white" : "border-neutral-200 bg-white text-neutral-600"}`}>{label}</button>)}</div></div>
          <div className="grid sm:grid-cols-3 gap-3">
            <label className="text-xs font-bold text-neutral-600">Deadline<input type="date" value={form.deadline} onChange={(event) => setForm({ ...form, deadline: event.target.value })} className="mt-1.5 h-11 w-full rounded-xl border border-neutral-200 px-3 text-sm" /></label>
            <label className="text-xs font-bold text-neutral-600">Zuständig<select value={form.assigned_to} onChange={(event) => setForm({ ...form, assigned_to: event.target.value })} className="mt-1.5 h-11 w-full rounded-xl border border-neutral-200 bg-white px-3 text-sm"><option value="">Noch offen</option>{members.map((member) => <option key={member.id} value={member.name}>{member.name}</option>)}</select></label>
            <label className="text-xs font-bold text-neutral-600">Priorität<select value={form.priority} onChange={(event) => setForm({ ...form, priority: event.target.value })} className="mt-1.5 h-11 w-full rounded-xl border border-neutral-200 bg-white px-3 text-sm"><option value="low">Niedrig</option><option value="medium">Normal</option><option value="high">Hoch</option></select></label>
          </div>
          <div className="rounded-2xl border border-dashed border-neutral-300 p-4"><label className="inline-flex cursor-pointer items-center gap-2 rounded-xl bg-neutral-100 px-3 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-200"><Upload className="h-4 w-4" /> {uploading ? "Wird hochgeladen …" : "Dateien/Referenzen hinzufügen"}<input type="file" multiple className="hidden" onChange={uploadFiles} disabled={uploading} /></label>{form.images.length > 0 && <div className="mt-3 flex flex-wrap gap-2">{form.images.map((item) => <span key={item.id} className="rounded-lg bg-neutral-100 px-2.5 py-1.5 text-xs text-neutral-600">{item.filename}</span>)}</div>}</div>
        </div>
        <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2"><button onClick={onClose} className="rounded-xl px-4 py-2.5 text-sm font-semibold text-neutral-600 hover:bg-neutral-100">Abbrechen</button><button data-testid="internal-design-submit" onClick={submit} disabled={saving || uploading} className="inline-flex items-center justify-center gap-2 rounded-xl bg-neutral-900 px-5 py-2.5 text-sm font-bold text-white disabled:opacity-50">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Auftrag anlegen</button></div>
      </div>
    </div>
  );
}

function DesignDetail({ item, accounts, currentKey, onClose, onChanged, onDeleted, isAdmin }) {
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [members, setMembers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [question, setQuestion] = useState("");
  const [asking, setAsking] = useState(false);
  const [uploadingResult, setUploadingResult] = useState(false);
  const [sendingResults, setSendingResults] = useState(false);
  const [comment, setComment] = useState("");
  const [commenting, setCommenting] = useState(false);
  const [pipelineAccount, setPipelineAccount] = useState("");
  const [pipelineType, setPipelineType] = useState("post");
  const [pipelineStatus, setPipelineStatus] = useState("idea");
  const [transferring, setTransferring] = useState(false);

  useEffect(() => {
    if (item) {
      setForm({ ...item }); setQuestion("");
      setPipelineAccount(item.pipeline_account_key || currentKey || accounts?.[0]?.key || "");
    }
  }, [item, currentKey, accounts]);
  useEffect(() => { api.get("/chat/members").then(({ data }) => setMembers(data)).catch(() => {}); }, []);

  if (!item || !form) return null;
  const f = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const cls = "w-full h-10 px-3 rounded-lg border border-neutral-200 text-sm bg-white";

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const payload = { event_title: form.event_title, description: form.description, start: form.start, end: form.end, deadline: form.deadline || "", organizer: form.organizer, partners: form.partners, sponsors: form.sponsors, formats: form.formats, status: form.status, assigned_to: form.assigned_to, contact_name: form.contact_name, contact_email: form.contact_email, contact_phone: form.contact_phone || "", ...extra };
      const { data } = await api.patch(`/design-requests/${item.id}`, payload);
      onChanged(data);
      toast.success("Gespeichert");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const ask = async () => {
    if (!question.trim()) return toast.error("Bitte Rückfrage eingeben");
    setAsking(true);
    try {
      const { data } = await api.post(`/design-requests/${item.id}/question`, { text: question });
      toast.success(data.emailed ? "Rückfrage gesendet — im Chat + per E-Mail mit Antwort-Link ✉️" : "Rückfrage im Team-Chat gepostet 💬 (keine E-Mail hinterlegt)");
      setQuestion("");
      if (data.request) onChanged(data.request);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setAsking(false); }
  };

  const uploadResults = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploadingResult(true);
    let results = [...(form.results || [])];
    for (const file of files) {
      if (file.size > 15 * 1024 * 1024) { toast.error(`„${file.name}“ ist größer als 15 MB`); continue; }
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/public/design-uploads", fd, { timeout: 120000 });
        results = [...results, data];
      } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || `Upload von „${file.name}“ fehlgeschlagen`); }
    }
    setUploadingResult(false);
    if (results.length !== (form.results || []).length) {
      setForm((p) => ({ ...p, results }));
      try {
        const { data } = await api.patch(`/design-requests/${item.id}`, { results });
        onChanged(data);
        toast.success("Design(s) hochgeladen");
      } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    }
  };

  const removeResult = async (id) => {
    const results = (form.results || []).filter((r) => r.id !== id);
    setForm((p) => ({ ...p, results }));
    try {
      const { data } = await api.patch(`/design-requests/${item.id}`, { results });
      onChanged(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const sendResults = async () => {
    setSendingResults(true);
    try {
      await api.post(`/design-requests/${item.id}/send-results`);
      toast.success("E-Mail mit Download-Link an den Absender gesendet ✉️");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSendingResults(false); }
  };

  const addComment = async () => {
    if (!comment.trim()) return toast.error("Bitte einen Kommentar eingeben");
    setCommenting(true);
    try {
      const { data } = await api.post(`/design-requests/${item.id}/comments`, { text: comment });
      setComment("");
      if (data.request) onChanged(data.request);
      toast.success(form.assigned_to ? `Feedback gesendet — ${form.assigned_to} wurde benachrichtigt 🔔` : "Feedback gespeichert");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setCommenting(false); }
  };

  const remove = async () => {
    try { await api.delete(`/design-requests/${item.id}`); toast.success("Anfrage gelöscht"); onDeleted(item.id); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const copyStudioLink = async () => {
    const link = `${window.location.origin}/insta-studio/${form.reply_token}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Geschützter Bearbeitungslink kopiert");
    } catch { toast.error(link); }
  };

  const transferToPipeline = async () => {
    if (!pipelineAccount) return toast.error("Bitte einen Zielkanal auswählen");
    setTransferring(true);
    try {
      const { data } = await api.post(`/design-requests/${item.id}/pipeline`, {
        account_key: pipelineAccount,
        content_type: pipelineType,
        status: pipelineStatus,
        assigned_creator: form.assigned_to || "",
      });
      if (data.request) onChanged(data.request);
      toast.success(data.existing ? "Die Anfrage ist bereits in der Pipeline" : "Anfrage in die Content-Pipeline übernommen");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setTransferring(false); }
  };

  const thread = form.thread || [];

  return (
    <Sheet open={!!item} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="design-detail">
        <SheetHeader><SheetTitle className="pr-8">{form.event_title}</SheetTitle></SheetHeader>
        <div className="space-y-3.5 mt-4 pb-8">
          <div className="grid grid-cols-2 gap-3">
            <L label="Status">
              <select data-testid="design-status" value={form.status} onChange={(e) => f("status", e.target.value)} className={cls}>
                {STATUS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </L>
            <L label="Zugewiesen an">
              <select data-testid="design-assign" value={form.assigned_to || ""} onChange={(e) => f("assigned_to", e.target.value)} className={cls}>
                <option value="">— Niemand —</option>
                {members.map((m) => <option key={m.id} value={m.name}>{m.name}</option>)}
              </select>
            </L>
          </div>
          <L label="Veranstaltung"><input data-testid="design-event" value={form.event_title} onChange={(e) => f("event_title", e.target.value)} className={cls} /></L>
          <L label="Inhalt / Beschreibung"><textarea value={form.description || ""} onChange={(e) => f("description", e.target.value)} rows={4} className="w-full px-3 py-2 rounded-lg border border-neutral-200 text-sm bg-white resize-none" /></L>
          <div className="grid grid-cols-2 gap-3">
            <L label="Beginn"><input type="datetime-local" value={form.start || ""} onChange={(e) => f("start", e.target.value)} className={cls} /></L>
            <L label="Ende"><input type="datetime-local" value={form.end || ""} onChange={(e) => f("end", e.target.value)} className={cls} /></L>
          </div>
          <L label="Deadline (Design fertig bis)"><input data-testid="design-deadline" type="date" value={form.deadline || ""} onChange={(e) => f("deadline", e.target.value)} className={cls} /></L>
          <L label="Veranstalter"><input value={form.organizer || ""} onChange={(e) => f("organizer", e.target.value)} className={cls} /></L>
          <L label="Partner"><input value={form.partners || ""} onChange={(e) => f("partners", e.target.value)} className={cls} /></L>
          <L label="Förderer"><input value={form.sponsors || ""} onChange={(e) => f("sponsors", e.target.value)} className={cls} /></L>
          <L label="Formate">
            <div className="flex gap-2 flex-wrap">
              {Object.entries(FORMAT_LABEL).map(([k, lbl]) => (
                <label key={k} className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-semibold cursor-pointer ${form.formats?.includes(k) ? "bg-violet-600 border-violet-600 text-white" : "bg-white border-neutral-200 text-neutral-500"}`}>
                  <input type="checkbox" className="hidden" checked={form.formats?.includes(k) || false} onChange={() => f("formats", form.formats?.includes(k) ? form.formats.filter((x) => x !== k) : [...(form.formats || []), k])} />{lbl}
                </label>
              ))}
            </div>
          </L>
          {(form.images || []).length > 0 && (
            <L label={`Angehängte Dateien (${form.images.length})`}>
              <div className="grid grid-cols-3 gap-2">
                {form.images.map((img, i) => (
                  <a key={img.id || i} href={resolveMediaUrl(img.url)} target="_blank" rel="noreferrer" data-testid={`design-image-${i}`} className="block rounded-lg overflow-hidden border border-neutral-200 bg-neutral-50">
                    {img.kind === "image" ? (
                      <img src={resolveMediaUrl(img.url)} alt={img.filename} className="h-20 w-full object-cover" />
                    ) : (
                      <span className="h-20 flex items-center justify-center text-[10px] text-neutral-500 px-1 text-center">{img.filename}</span>
                    )}
                  </a>
                ))}
              </div>
            </L>
          )}
          <div className="grid grid-cols-2 gap-3">
            <L label="Kontakt"><input value={form.contact_name || ""} onChange={(e) => f("contact_name", e.target.value)} className={cls} /></L>
            <L label="E-Mail"><input value={form.contact_email || ""} onChange={(e) => f("contact_email", e.target.value)} className={cls} /></L>
          </div>
          <L label="Telefon"><input data-testid="design-phone" value={form.contact_phone || ""} onChange={(e) => f("contact_phone", e.target.value)} className={cls} /></L>

          <div className="rounded-xl border border-cyan-200 bg-cyan-50 p-3.5" data-testid="design-pipeline-transfer">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <p className="text-xs font-bold text-cyan-900">Übergabe an die Content-Pipeline</p>
                <p className="text-[11px] text-cyan-700 mt-1">Externe bearbeiten nur diesen Entwurf. Euer Team entscheidet Kanal, Format und Startpunkt.</p>
              </div>
              <button type="button" data-testid="design-copy-studio-link" onClick={copyStudioLink} className="shrink-0 inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-lg bg-white border border-cyan-200 text-cyan-800 hover:bg-cyan-100 transition">
                <Copy className="h-3.5 w-3.5" /> Bearbeitungslink
              </button>
            </div>
            {form.pipeline_content_id ? (
              <div className="flex items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-white p-3" data-testid="design-pipeline-linked">
                <div className="flex items-center gap-2 min-w-0"><CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" /><span className="text-xs text-neutral-700"><strong>Bereits übernommen</strong><br />Kanal: {accounts.find((a) => a.key === form.pipeline_account_key)?.name || form.pipeline_account_key}</span></div>
                <button type="button" onClick={() => { onClose(); navigate("/pipeline"); }} className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition"><ExternalLink className="h-3.5 w-3.5" /> Pipeline öffnen</button>
              </div>
            ) : (
              <>
                <div className="grid sm:grid-cols-3 gap-2">
                  <L label="Zielkanal"><select data-testid="design-pipeline-account" value={pipelineAccount} onChange={(e) => setPipelineAccount(e.target.value)} className={cls}>{accounts.map((a) => <option key={a.key} value={a.key}>{a.name}</option>)}</select></L>
                  <L label="Format"><select data-testid="design-pipeline-type" value={pipelineType} onChange={(e) => setPipelineType(e.target.value)} className={cls}><option value="post">Post</option><option value="carousel">Karussell</option><option value="reel">Reel</option><option value="story">Story</option></select></L>
                  <L label="Start in"><select data-testid="design-pipeline-status" value={pipelineStatus} onChange={(e) => setPipelineStatus(e.target.value)} className={cls}><option value="idea">Idee</option><option value="draft">In Arbeit</option><option value="review">Freigabe</option></select></L>
                </div>
                <button type="button" data-testid="design-pipeline-submit" onClick={transferToPipeline} disabled={transferring || !pipelineAccount} className="mt-3 w-full h-10 rounded-xl bg-cyan-700 text-white text-xs font-bold hover:bg-cyan-800 transition disabled:opacity-50 flex items-center justify-center gap-2">
                  {transferring ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />} In Pipeline übernehmen
                </button>
              </>
            )}
          </div>

          <div className="rounded-xl border border-violet-200 bg-violet-50 p-3.5">
            <p className="text-xs font-bold text-violet-800 mb-2">💬 Rückfragen & Antworten</p>
            {thread.length > 0 && (
              <div className="space-y-2 mb-3 max-h-56 overflow-y-auto" data-testid="design-thread">
                {thread.map((m) => (
                  <div key={m.id} className={`flex ${m.from === "designer" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[88%] rounded-xl px-3 py-2 text-xs ${m.from === "designer" ? "bg-violet-600 text-white" : "bg-white border border-violet-200 text-neutral-800"}`}>
                      <p className={`text-[9px] font-bold mb-0.5 ${m.from === "designer" ? "text-violet-200" : "text-violet-600"}`}>{m.from === "designer" ? m.author || "Design-Team" : `${m.author || "Absender"} (Antwort)`}</p>
                      <p className="whitespace-pre-wrap">{m.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <textarea data-testid="design-question-input" value={question} onChange={(e) => setQuestion(e.target.value)} rows={2} placeholder="z. B. Welches Logo soll verwendet werden?" className="w-full px-3 py-2 rounded-lg border border-violet-200 text-sm bg-white resize-none" />
            <button data-testid="design-question-send" onClick={ask} disabled={asking} className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold px-3.5 py-2 rounded-lg bg-violet-600 text-white hover:bg-violet-700 transition disabled:opacity-50">
              {asking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Rückfrage senden{form.contact_email ? " (Chat + E-Mail mit Antwort-Link)" : " (Team-Chat)"}
            </button>
          </div>

          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3.5">
            <p className="text-xs font-bold text-emerald-800 mb-2">🎨 Fertige Designs</p>
            {(form.results || []).length > 0 && (
              <div className="space-y-1.5 mb-2">
                {form.results.map((r, i) => (
                  <div key={r.id || i} data-testid={`design-result-${i}`} className="flex items-center gap-2 bg-white rounded-lg border border-emerald-200 px-2.5 py-1.5 text-xs text-neutral-700">
                    <a href={resolveMediaUrl(r.url)} target="_blank" rel="noreferrer" className="truncate flex-1 font-semibold hover:underline">{r.filename}</a>
                    <button onClick={() => removeResult(r.id)} className="text-neutral-400 hover:text-rose-500"><X className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <label data-testid="design-result-upload" className="inline-flex items-center gap-1.5 text-xs font-bold px-3.5 py-2 rounded-lg bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-100 transition cursor-pointer">
                {uploadingResult ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Design hochladen
                <input type="file" multiple className="hidden" onChange={uploadResults} disabled={uploadingResult} />
              </label>
              <button data-testid="design-send-results" onClick={sendResults} disabled={sendingResults || (form.results || []).length === 0 || !form.contact_email}
                title={!form.contact_email ? "Keine E-Mail-Adresse hinterlegt" : ""}
                className="inline-flex items-center gap-1.5 text-xs font-bold px-3.5 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition disabled:opacity-50">
                {sendingResults ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />} Per E-Mail ausliefern
              </button>
            </div>
            {!form.contact_email && <p className="text-[10px] text-emerald-700 mt-1.5">Ohne E-Mail-Adresse: Designs intern über die Anfrage teilen oder Download-Links weitergeben.</p>}
          </div>

          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3.5" data-testid="design-comments">
            <p className="text-xs font-bold text-amber-800 mb-2">📝 Feedback an den Designer{form.assigned_to ? ` (${form.assigned_to})` : ""}</p>
            {(form.comments || []).length > 0 && (
              <div className="space-y-2 mb-3 max-h-56 overflow-y-auto" data-testid="design-comments-list">
                {form.comments.map((c, i) => (
                  <div key={c.id || i} data-testid={`design-comment-${i}`} className="bg-white rounded-xl border border-amber-200 px-3 py-2 text-xs text-neutral-800">
                    <p className="text-[9px] font-bold text-amber-600 mb-0.5">
                      {c.author}{c.created_at ? ` · ${new Date(c.created_at).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })}` : ""}
                    </p>
                    <p className="whitespace-pre-wrap">{c.text}</p>
                  </div>
                ))}
              </div>
            )}
            <textarea data-testid="design-comment-input" value={comment} onChange={(e) => setComment(e.target.value)} rows={2}
              placeholder="z. B. Bitte das Logo größer machen und Datum korrigieren" className="w-full px-3 py-2 rounded-lg border border-amber-200 text-sm bg-white resize-none" />
            <button data-testid="design-comment-send" onClick={addComment} disabled={commenting}
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold px-3.5 py-2 rounded-lg bg-amber-600 text-white hover:bg-amber-700 transition disabled:opacity-50">
              {commenting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Feedback senden{form.assigned_to ? " (mit Push-Benachrichtigung)" : ""}
            </button>
            <p className="text-[10px] text-amber-700 mt-1.5">Intern sichtbar — der zugewiesene Designer bekommt sofort eine Benachrichtigung.</p>
          </div>

          <div className="flex items-center gap-2 pt-1">
            <button data-testid="design-save" onClick={() => save()} disabled={saving} className="flex-1 h-11 rounded-xl bg-neutral-900 text-white text-sm font-bold hover:bg-neutral-800 transition disabled:opacity-50 flex items-center justify-center gap-2">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Speichern
            </button>
            {isAdmin && (
              <button data-testid="design-delete" onClick={remove} title="Löschen" className="h-11 w-11 rounded-xl border border-rose-200 text-rose-500 hover:bg-rose-50 flex items-center justify-center">
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function L({ label, children }) {
  return (
    <div>
      <label className="text-[11px] font-bold uppercase tracking-wide text-neutral-400">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
